import React, { useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "../components/Logo";
import PasswordInput from "../components/PasswordInput";
import { PublicFooter } from "../components/PublicFooter";

const LOGIN_BG = `${process.env.PUBLIC_URL || ""}/login-bg.png`;

export default function Login() {
    const { user, login, verifyMfa } = useAuth();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [challenge, setChallenge] = useState("");
    const [code, setCode] = useState("");

    if (user && user !== false) {
        const dest = location.state?.from?.pathname || (user.role === "admin" ? "/admin" : "/dashboard");
        return <Navigate to={dest} replace />;
    }

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const result = await login(email.trim(), password);
        setSubmitting(false);
        if (result.mfaRequired) {
            setChallenge(result.challenge);
            return;
        }
        if (!result.ok) setError(result.error || "Unable to sign in.");
    }

    async function onSubmitCode(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const result = await verifyMfa(challenge, code.trim());
        setSubmitting(false);
        if (!result.ok) {
            setError(result.error || "Invalid code.");
            setChallenge("");
            setCode("");
        }
    }

    return (
        <div
            className="relative min-h-screen w-full overflow-hidden bg-[#0a0a0a] flex items-center justify-center px-6 py-28"
            data-testid="login-page"
        >
            {/* Ambient background */}
            <div
                className="absolute inset-0 bg-cover bg-center opacity-50"
                style={{ backgroundImage: `url(${LOGIN_BG})` }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/60 to-black/90" aria-hidden="true" />
            <div className="absolute inset-0 bg-grid-faint opacity-40" aria-hidden="true" />

            {/* Top brand */}
            <div className="absolute top-8 left-8 z-10">
                <Logo />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 w-full max-w-[400px]"
            >
                <div className="mb-10 text-left">
                    <p className="text-[11.5px] uppercase tracking-[0.18em] text-zinc-500 mb-3">
                        Command Center · Managed by WebJesters
                    </p>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
                        Welcome back
                    </h1>
                    <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed">
                        Your operational portal into the WebJesters ecosystem.
                    </p>
                </div>

                {challenge ? (
                <form onSubmit={onSubmitCode} className="space-y-5" data-testid="mfa-form">
                    <div className="flex items-center gap-2 text-[13px] text-zinc-300">
                        <ShieldCheck className="h-4 w-4 text-emerald-300" /> Two-factor authentication
                    </div>
                    <div className="space-y-2">
                        <label htmlFor="mfa-code" className="block text-[12px] text-zinc-400 font-medium">
                            6-digit code from your authenticator app (or a recovery code)
                        </label>
                        <input
                            id="mfa-code"
                            autoFocus
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            required
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            data-testid="mfa-code-input"
                            className="w-full h-11 rounded-md bg-zinc-950/60 border border-zinc-800 px-3 text-[16px] tracking-[0.3em] text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-500"
                            placeholder="123456"
                        />
                    </div>
                    {error && (
                        <div className="text-[13px] text-red-400/90 border border-red-900/40 bg-red-950/30 rounded-md px-3 py-2" data-testid="mfa-error">{error}</div>
                    )}
                    <button type="submit" disabled={submitting || code.trim().length < 6} data-testid="mfa-submit-button"
                        className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-md bg-zinc-50 text-zinc-950 text-[14px] font-medium hover:bg-white disabled:opacity-70">
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify <ArrowRight className="h-4 w-4" /></>}
                    </button>
                    <button type="button" onClick={() => { setChallenge(""); setCode(""); setError(""); }} data-testid="mfa-back-button"
                        className="w-full text-[12.5px] text-zinc-500 hover:text-zinc-200">Back to sign in</button>
                </form>
                ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                    <div className="space-y-2">
                        <label htmlFor="email" className="block text-[12px] text-zinc-400 font-medium">
                            Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="login-email-input"
                            className="w-full h-11 rounded-md bg-zinc-950/60 border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-all focus:border-zinc-500 focus:bg-zinc-900/60"
                            placeholder="you@business.com"
                        />
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-baseline justify-between">
                            <label htmlFor="password" className="block text-[12px] text-zinc-400 font-medium">
                                Password
                            </label>
                            <Link
                                to="/forgot-password"
                                data-testid="login-forgot-link"
                                className="text-[11.5px] text-zinc-500 hover:text-zinc-200 transition-colors"
                            >
                                Forgot password?
                            </Link>
                        </div>
                        <PasswordInput
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            testid="login-password-input"
                        />
                    </div>

                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-[13px] text-red-400/90 border border-red-900/40 bg-red-950/30 rounded-md px-3 py-2"
                            data-testid="login-error"
                        >
                            {error}
                        </motion.div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="login-submit-button"
                        className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-md bg-zinc-50 text-zinc-950 text-[14px] font-medium transition-colors hover:bg-white disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Signing in
                            </>
                        ) : (
                            <>
                                Sign in
                                <ArrowRight className="h-4 w-4" />
                            </>
                        )}
                    </button>
                </form>
                )}

                <div className="mt-8 pt-6 border-t border-zinc-900">
                    <p className="text-[12px] text-zinc-500 leading-relaxed">
                        Access is provisioned by WebJesters. Reach out to your account lead to add a new user to your workspace.
                    </p>
                </div>
            </motion.div>

            <PublicFooter prefix="login" />
        </div>
    );
}
